import type { ReactNode } from 'react';
import { Typography } from '@arco-design/web-react';
import {
  IconApps,
  IconDashboard,
  IconPoweroff,
  IconSafe,
  IconSwap,
  IconUser,
} from '@arco-design/web-react/icon';
import logo from '../assets/company-logo.png';
import type { RuntimePrimaryNavigationItem } from '@jukun/platform-sdk/auth';
import type { PortalApplication, PortalIdentity } from '../model/portalModels';
import { isApplicationPathname } from '../navigation/applicationSelection';
import {
  fixedPortalNavigation, isFixedPortalPath, resolveFixedPortalLeaf,
} from '../navigation/portalNavigation';
import { ApplicationSwitcher } from './ApplicationSwitcher';

const { Text } = Typography;

function navigationIcon(code: string): ReactNode {
  const value = code.toLowerCase();
  if (value.includes('identity') || value.includes('permission') || value.includes('system')) {
    return <IconSafe />;
  }
  if (value.includes('integration') || value.includes('business') || value.includes('commerce')) {
    return <IconSwap />;
  }
  if (value.includes('my')) return <IconUser />;
  if (value.includes('workbench') || value.includes('project')) return <IconDashboard />;
  return <IconApps />;
}

export function PortalShell(props: {
  identity: PortalIdentity;
  applications: PortalApplication[];
  currentApplication?: PortalApplication;
  primaryNavigation: readonly RuntimePrimaryNavigationItem[];
  primaryNavigationLoading?: boolean;
  applicationSwitchingTo?: string;
  pathname: string;
  activePrimaryNavigationCode?: string;
  onNavigate: (path: string) => void;
  onSelectApplication: (application: PortalApplication) => void;
  onPrimaryNavigationSelect: (navigationCode: string) => void;
  onLogout: () => void;
  children: ReactNode;
}) {
  const actorName = props.identity.actorName.trim() || '未设置';
  const fixedActive = isFixedPortalPath(props.pathname);
  const activeFixedLeaf = resolveFixedPortalLeaf(props.pathname);
  const activeFixedModule = fixedPortalNavigation.modules.find(module =>
    module.key === activeFixedLeaf?.moduleKey);
  const activeNavigation = fixedActive ? undefined
    : props.primaryNavigation.find(item =>
      item.code === props.activePrimaryNavigationCode);
  const monitorBoardActive = !fixedActive
    && props.currentApplication?.appCode === 'JUKUN_MONITOR'
    && props.currentApplication.status === 'ENABLED'
    && isApplicationPathname(props.currentApplication, props.pathname)
    && activeNavigation?.code === 'JUKUN_MONITOR_BOARD_ACCESS';
  const applicationContextLabel = props.currentApplication?.appName ?? '暂无可用应用';

  return (
    <div className={[
      'platform-portal-shell reference-navigation platform-portal-shell--portal',
      monitorBoardActive ? 'platform-portal-shell--monitor-board' : '',
    ].filter(Boolean).join(' ')}>
      <header className="ui-app-topbar">
        <div className="ui-app-topbar__brand">
          <span className="ui-app-topbar__logo ui-app-topbar__logo--company">
            <img src={logo} alt="巨鲲科技" draggable={false} />
          </span>
          <span className="ui-app-topbar__title">
            <strong>巨鲲云平台</strong>
            <span>{applicationContextLabel}</span>
          </span>
        </div>
        <div className="ui-app-topbar__actions">
          <button className="ui-app-topbar__action" type="button"
            disabled={Boolean(props.applicationSwitchingTo)}
            onClick={() => props.onNavigate('/portal/todos')}>我的任务</button>
          <button className="ui-app-topbar__action" type="button"
            disabled={Boolean(props.applicationSwitchingTo)}
            onClick={() => props.onNavigate('/portal/personal')}>个人中心</button>
          <button className="ui-app-topbar__user" type="button"
            disabled={Boolean(props.applicationSwitchingTo)}
            onClick={() => props.onNavigate('/portal/personal')}>
            <span className="ui-app-topbar__avatar portal-user-avatar portal-user-avatar--topbar">
              <IconUser className="portal-user-avatar__fallback" aria-hidden="true" />
              {props.identity.avatarUrl?.trim()
                ? <img className="portal-user-avatar__image"
                    src={props.identity.avatarUrl.trim()} alt=""
                    referrerPolicy="no-referrer"
                    onError={event => { event.currentTarget.hidden = true; }} /> : null}
            </span>
            <span className="ui-app-topbar__user-body">
              <strong>{actorName}</strong>
              <span>{props.identity.tenantName} · {props.identity.platformOperator ? '平台管理员' : '普通用户'}</span>
            </span>
          </button>
          <button className="ui-app-topbar__action ui-app-topbar__action--icon" type="button"
            aria-label="退出登录" onClick={props.onLogout}><IconPoweroff /></button>
        </div>
      </header>

      <div className="reference-navigation__body">
        <div className={[
          'ui-side-nav-frame',
          fixedActive ? '' : 'platform-portal-frame--application',
        ].filter(Boolean).join(' ')}>
          <aside className="ui-side-nav-frame__rail">
            <div className="ui-side-nav-frame__rail-main">
              <ApplicationSwitcher applications={props.applications}
                activeApplication={props.currentApplication}
                disabled={Boolean(props.applicationSwitchingTo)}
                onSelect={props.onSelectApplication} />
              <button className={[
                'ui-side-nav-frame__rail-item', fixedActive ? 'ui-side-nav-frame__rail-item--active' : '',
              ].filter(Boolean).join(' ')} type="button" aria-label="我的域"
              disabled={Boolean(props.applicationSwitchingTo)}
              onClick={() => props.onNavigate('/portal/workbench')}>
                <i aria-hidden="true">{navigationIcon(fixedPortalNavigation.code)}</i>
                <span>{fixedPortalNavigation.label}</span>
              </button>
              <div className="portal-rail-divider" aria-label="以下导航由当前应用注入" />
              {props.primaryNavigationLoading ? (
                <div className="portal-primary-navigation-loading" role="status"
                  aria-label="正在加载当前应用一级导航">
                  <i className="portal-primary-navigation-loading__indicator" aria-hidden="true" />
                  <span>加载中</span>
                </div>
              ) : props.primaryNavigation.map(navigation => (
                <button className={[
                  'ui-side-nav-frame__rail-item',
                  navigation.code === activeNavigation?.code ? 'ui-side-nav-frame__rail-item--active' : '',
                ].filter(Boolean).join(' ')} key={navigation.code} type="button"
                disabled={Boolean(props.applicationSwitchingTo)}
                aria-label={`${navigation.label}一级导航`}
                onClick={() => props.onPrimaryNavigationSelect(navigation.code)}>
                  <i aria-hidden="true">{navigationIcon(navigation.code)}</i>
                  <span>{navigation.label}</span>
                </button>
              ))}
            </div>
          </aside>

          {fixedActive ? (
            <aside className="ui-side-nav-frame__secondary">
              <div className="ui-side-nav-frame__secondary-head">
                <Text type="secondary">巨鲲云平台</Text>
                <strong>{fixedPortalNavigation.label}</strong>
              </div>
              <div className="ui-side-nav-frame__secondary-list">
                {fixedPortalNavigation.modules.map(module => (
                  <div className={[
                    'ui-side-nav-frame__module',
                    module.key === activeFixedModule?.key ? 'ui-side-nav-frame__module--active' : '',
                  ].filter(Boolean).join(' ')} key={module.key}>
                    <button className="ui-side-nav-frame__module-title" type="button"
                      disabled={Boolean(props.applicationSwitchingTo)}
                      onClick={() => props.onNavigate(module.leaves[0].path)}>{module.title}</button>
                    <div className="ui-side-nav-frame__leaf-list">
                      {module.leaves.map(item => (
                        <button className={item.path === activeFixedLeaf?.path ? 'active' : ''}
                          key={item.code} type="button"
                          disabled={Boolean(props.applicationSwitchingTo)}
                          onClick={() => props.onNavigate(item.path)}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </aside>
          ) : null}

          <section className="ui-side-nav-frame__content platform-portal-frame-content">
            {fixedActive ? <div className="ui-side-nav-frame__content-head">
              <div className="platform-portal-breadcrumb">
                <span>巨鲲云平台</span><span aria-hidden="true">/</span>
                <span>{activeFixedModule?.title ?? '页面'}</span><span aria-hidden="true">/</span>
                <span className="platform-portal-breadcrumb__current">
                  {activeFixedLeaf?.label}
                </span>
              </div>
            </div> : null}
            <div className={fixedActive ? [
              'platform-portal-page-body',
              activeFixedLeaf?.path === '/portal/todos'
                || activeFixedLeaf?.path === '/portal/drafts'
                ? 'platform-portal-page-body--todos' : '',
              activeFixedLeaf?.path === '/portal/workbench'
                ? 'platform-portal-page-body--workbench' : '',
              activeFixedLeaf?.path === '/portal/operations'
                || activeFixedLeaf?.path === '/portal/personal'
                ? 'platform-portal-page-body--edge-aligned' : '',
            ].filter(Boolean).join(' ') : 'platform-portal-app-body'}>
              {props.children}
            </div>
          </section>
        </div>
      </div>
      {props.applicationSwitchingTo ? (
        <div className="portal-application-switch-overlay" role="status"
          aria-live="polite" aria-busy="true">
          <div className="portal-application-switch-overlay__panel">
            <i className="portal-application-switch-overlay__indicator" aria-hidden="true" />
            <strong>{props.applicationSwitchingTo}</strong>
            <span>正在加载应用</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
